if (!customElements.get('buybox-frequency')) {
  customElements.define(
    'buybox-frequency',
    class BuyboxFrequency extends HTMLElement {
      constructor() {
        super();
        this.onOnetimeClick = this.onOnetimeClick.bind(this);
        this.onSubscribeClick = this.onSubscribeClick.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDropdownKeydown = this.onDropdownKeydown.bind(this);
      }

      connectedCallback() {
        this.root = this.closest('[data-section]') || document;
        this.btnOnetime = this.querySelector('[data-frequency-onetime]');
        this.btnSubscribe = this.querySelector('[data-frequency-subscribe]');
        this.dropdownWrap = this.querySelector('[data-frequency-dropdown]');
        this.select = this.querySelector('[data-frequency-select]');
        if (!this.btnOnetime || !this.btnSubscribe || !this.dropdownWrap || !this.select) return;

        this.btnOnetime.addEventListener('click', this.onOnetimeClick);
        this.btnSubscribe.addEventListener('click', this.onSubscribeClick);
        this.select.addEventListener('change', this.onSelectChange);
        document.addEventListener('click', this.onDocumentClick);

        this.waitForLoopOptions();
      }

      disconnectedCallback() {
        this.btnOnetime?.removeEventListener('click', this.onOnetimeClick);
        this.btnSubscribe?.removeEventListener('click', this.onSubscribeClick);
        this.select?.removeEventListener('change', this.onSelectChange);
        document.removeEventListener('click', this.onDocumentClick);
        this.observer?.disconnect();
      }

      get loopWidget() {
        return this.root.querySelector('.loop-widget-container');
      }

      get loopSelect() {
        return this.loopWidget?.querySelector('select.loop-widget-sp-selector') ?? null;
      }

      waitForLoopOptions() {
        if (this.populateOptions()) return;

        this.observer = new MutationObserver(() => {
          if (this.populateOptions()) this.observer.disconnect();
        });
        this.observer.observe(this.root, { childList: true, subtree: true });
      }

      populateOptions() {
        const loopSelect = this.loopSelect;
        if (!loopSelect || loopSelect.options.length === 0) return false;

        Array.from(loopSelect.options).forEach((loopOption) => {
          const option = document.createElement('option');
          option.value = loopOption.value;
          option.textContent = loopOption.text;
          this.select.appendChild(option);
        });

        this.buildDropdown();
        return true;
      }

      buildDropdown() {
        const { popularOption, popularBadge } = this.dataset;
        const popularMatch = popularOption?.trim().toLowerCase();
        const options = Array.from(this.select.options);

        this.select.classList.add('visually-hidden');
        this.select.tabIndex = -1;

        this.dropdown = document.createElement('div');
        this.dropdown.className = 'custom-freq-dropdown';
        this.dropdown.addEventListener('keydown', this.onDropdownKeydown);

        this.toggleButton = document.createElement('button');
        this.toggleButton.type = 'button';
        this.toggleButton.className = 'custom-freq-dropdown__selected';
        this.toggleButton.setAttribute('aria-expanded', 'false');
        this.toggleButton.setAttribute('aria-label', this.select.getAttribute('aria-label') ?? '');
        this.selectedText = document.createElement('span');
        this.selectedText.className = 'custom-freq-dropdown__item-text';
        this.selectedText.textContent = options[0]?.text ?? '';
        this.toggleButton.appendChild(this.selectedText);
        this.toggleButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.setOpen(!this.dropdown.classList.contains('is-open'));
        });

        const list = document.createElement('div');
        list.className = 'custom-freq-dropdown__list';

        this.items = options.map((option, index) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.className = 'custom-freq-dropdown__item';
          item.dataset.value = option.value;
          item.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');
          if (index === 0) item.classList.add('is-selected');

          const text = document.createElement('span');
          text.className = 'custom-freq-dropdown__item-text';
          text.textContent = option.text;
          item.appendChild(text);

          if (popularMatch && popularBadge && option.text.toLowerCase().includes(popularMatch)) {
            const badge = document.createElement('span');
            badge.className = 'freq-most-popular-badge';
            badge.textContent = popularBadge;
            item.appendChild(badge);
          }

          item.addEventListener('click', (event) => {
            event.stopPropagation();
            this.chooseItem(item, option);
          });

          list.appendChild(item);
          return item;
        });

        this.dropdown.append(this.toggleButton, list);
        this.dropdownWrap.insertBefore(this.dropdown, this.select);
      }

      chooseItem(item, option) {
        this.select.value = option.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.items.forEach((el) => {
          el.classList.toggle('is-selected', el === item);
          el.setAttribute('aria-pressed', el === item ? 'true' : 'false');
        });
        this.selectedText.textContent = option.text;
        this.setOpen(false);
        this.toggleButton.focus();
      }

      setOpen(open) {
        if (!this.dropdown) return;
        this.dropdown.classList.toggle('is-open', open);
        this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      }

      onDropdownKeydown(event) {
        if (event.key !== 'Escape') return;
        this.setOpen(false);
        this.toggleButton.focus();
      }

      onDocumentClick() {
        this.setOpen(false);
      }

      onSelectChange() {
        const loopSelect = this.loopSelect;
        if (!loopSelect) return;
        loopSelect.value = this.select.value;
        loopSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onOnetimeClick() {
        this.choosePurchaseType(this.btnOnetime, '[data-loop-widget-onetime-purchase-option]');
      }

      onSubscribeClick() {
        this.choosePurchaseType(this.btnSubscribe, '[data-loop-widget-selling-plan-group]');
      }

      choosePurchaseType(button, loopOptionSelector) {
        [this.btnOnetime, this.btnSubscribe].forEach((btn) => {
          btn.classList.toggle('is-selected', btn === button);
          btn.setAttribute('aria-pressed', btn === button ? 'true' : 'false');
        });

        const isSubscribe = button === this.btnSubscribe;
        this.dropdownWrap.hidden = !isSubscribe;
        this.dropdownWrap.setAttribute('aria-hidden', isSubscribe ? 'false' : 'true');
        if (!isSubscribe) this.setOpen(false);

        this.loopWidget?.querySelector(loopOptionSelector)?.click();
      }
    }
  );
}
